import React, { useEffect, useState, useCallback } from 'react';
import api from '../../utils/api';
import { RefreshCw, Activity, AlertTriangle, Pause, ChevronDown, ChevronUp } from 'lucide-react';

const LiveDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedMachine, setExpandedMachine] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const r = await api.get('/production/live-dashboard');
      setData(r.data);
    } catch(e) { console.error(e); } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(fetchData, 15000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchData]);

  const getMachineColor = (status) => {
    const map = { running: '#059669', idle: '#D97706', down: '#DC2626', maintenance: '#6366F1' };
    return map[status] || '#9CA3AF';
  };

  const getMachineBg = (status) => {
    const map = { running: '#ECFDF5', idle: '#FFFBEB', down: '#FEF2F2', maintenance: '#EEF2FF' };
    return map[status] || '#F9FAFB';
  };

  if (loading) return <div style={{ padding: '40px', textAlign: 'center' }}>Loading dashboard...</div>;
  if (!data) return <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-tertiary)' }}>Failed to load dashboard</div>;

  const { machines, machine_summary, active_orders, today_production, today_downtime } = data;
  const totalOutput = today_production.good + today_production.rejected + today_production.scrap;
  const yieldPct = totalOutput > 0 ? ((today_production.good / totalOutput) * 100).toFixed(1) : '0.0';

  return (
    <div data-testid="live-dashboard-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: 700, margin: 0 }}>Live Production Dashboard</h1>
          <p style={{ margin: '4px 0 0', color: 'var(--text-tertiary)', fontSize: '13px' }}>Execution Layer - Real-time machine and production monitoring</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setAutoRefresh(!autoRefresh)} className={`btn btn-sm ${autoRefresh ? 'btn-primary' : 'btn-secondary'}`} data-testid="auto-refresh-btn">
            <Activity size={14} /> {autoRefresh ? 'Live' : 'Paused'}
          </button>
          <button onClick={fetchData} className="btn btn-secondary btn-sm" data-testid="refresh-btn"><RefreshCw size={14} /> Refresh</button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 mb-4">
        <div className="stat-card" style={{ borderLeft: '4px solid #059669' }} data-testid="stat-running">
          <div className="stat-value" style={{ color: '#059669' }}>{machine_summary.running}</div>
          <div className="stat-label">Running</div>
        </div>
        <div className="stat-card" style={{ borderLeft: '4px solid #D97706' }} data-testid="stat-idle">
          <div className="stat-value" style={{ color: '#D97706' }}>{machine_summary.idle}</div>
          <div className="stat-label">Idle</div>
        </div>
        <div className="stat-card" style={{ borderLeft: '4px solid #DC2626' }} data-testid="stat-down">
          <div className="stat-value" style={{ color: '#DC2626' }}>{machine_summary.down}</div>
          <div className="stat-label">Down</div>
        </div>
        <div className="stat-card" style={{ borderLeft: '4px solid #0047AB' }} data-testid="stat-orders">
          <div className="stat-value">{active_orders}</div>
          <div className="stat-label">Active Orders</div>
        </div>
      </div>

      {/* Today's Production Summary */}
      <div className="grid grid-cols-4 mb-4">
        <div className="stat-card" data-testid="stat-good">
          <div className="stat-value" style={{ color: '#059669' }}>{today_production.good}</div>
          <div className="stat-label">Good Output</div>
        </div>
        <div className="stat-card" data-testid="stat-rejected">
          <div className="stat-value" style={{ color: '#DC2626' }}>{today_production.rejected}</div>
          <div className="stat-label">Rejected</div>
        </div>
        <div className="stat-card" data-testid="stat-scrap">
          <div className="stat-value" style={{ color: '#D97706' }}>{today_production.scrap}</div>
          <div className="stat-label">Scrap</div>
        </div>
        <div className="stat-card" data-testid="stat-yield">
          <div className="stat-value">{yieldPct}%</div>
          <div className="stat-label">First Pass Yield</div>
        </div>
      </div>

      {/* Machine Tiles */}
      <div className="card mb-4">
        <div className="card-header"><h3 className="card-title">Machine Status Grid ({machine_summary.total} machines)</h3></div>
        {machines.length === 0 ? (
          <p style={{ color: 'var(--text-tertiary)', textAlign: 'center', padding: '20px' }}>No machines configured. Add machines in Production Masters.</p>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '12px' }}>
            {machines.map(m => (
              <div
                key={m.id}
                data-testid={`machine-tile-${m.machine_code}`}
                onClick={() => setExpandedMachine(expandedMachine === m.id ? null : m.id)}
                style={{
                  background: getMachineBg(m.current_status),
                  border: `2px solid ${getMachineColor(m.current_status)}`,
                  borderRadius: '6px', padding: '14px', cursor: 'pointer',
                  transition: 'transform 150ms ease, box-shadow 150ms ease',
                }}
              >
                <div className="flex items-center justify-between" style={{ marginBottom: '6px' }}>
                  <span className="font-mono" style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>{m.machine_code}</span>
                  <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: getMachineColor(m.current_status), boxShadow: m.current_status === 'running' ? `0 0 8px ${getMachineColor(m.current_status)}` : 'none' }} />
                </div>
                <div style={{ fontWeight: 600, fontSize: '15px', marginBottom: '4px' }}>{m.name}</div>
                <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                  {m.current_status === 'running' && m.current_sku && <>Running: {m.current_sku}</>}
                  {m.current_status === 'idle' && <><Pause size={12} style={{ display: 'inline', verticalAlign: 'middle' }} /> Idle</>}
                  {m.current_status === 'down' && <><AlertTriangle size={12} style={{ display: 'inline', verticalAlign: 'middle' }} /> Down</>}
                  {m.current_status === 'maintenance' && 'Under Maintenance'}
                  {!m.current_status && <><Pause size={12} style={{ display: 'inline', verticalAlign: 'middle' }} /> Idle</>}
                </div>
                {m.current_operator_name && <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '4px' }}>Operator: {m.current_operator_name}</div>}
                <div style={{ textAlign: 'right', marginTop: '4px' }}>
                  {expandedMachine === m.id ? <ChevronUp size={14} color="var(--text-tertiary)" /> : <ChevronDown size={14} color="var(--text-tertiary)" />}
                </div>
                {expandedMachine === m.id && (
                  <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid var(--border-default)', fontSize: '12px' }}>
                    <div>Type: {m.machine_type || '-'}</div>
                    <div>Location: {m.location || '-'}</div>
                    <div>Capacity: {m.capacity_per_hour || '-'}/hr</div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Today's Downtime */}
      {today_downtime && today_downtime.length > 0 && (
        <div className="card">
          <div className="card-header"><h3 className="card-title">Today's Downtime Events ({today_downtime.length})</h3></div>
          <div className="table-container">
            <table>
              <thead><tr><th>Machine</th><th>Reason</th><th>Duration (min)</th><th>Notes</th></tr></thead>
              <tbody>
                {today_downtime.map(d => (
                  <tr key={d.id}>
                    <td>{d.machine_name || '-'}</td>
                    <td>{d.reason || '-'}</td>
                    <td className="tabular-nums">{d.duration_minutes || '-'}</td>
                    <td>{d.notes || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default LiveDashboard;
